/** @jsxImportSource @emotion/react */

import { CalciteLabel, CalciteLoader } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { useContext, useEffect, useRef, useState } from "react";
import { ReactTabulator } from "react-tabulator";
import {
  getDiscrepancyColumn,
  getDiscrepancyExportColumns,
} from "services/DiscrepancyRecord.d";
import tw from "twin.macro";

import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import "react-tabulator/lib/styles.css";

import ExportButton from "components/ExportButton";
import FiltersDisplay from "components/FiltersDisplay";
import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Criteria } from "services/APIBase.d";
import RecordService from "services/RecordService";
import { updateFooterText } from "utils/helpers";
import { UserContext } from "../../context/UserContext";

const client = new RecordService("discrep");
const DiscrepancyRecords: React.FC = () => {
  const [pageSize, setPageSize] = useState<number>(25);
  const [criterias, setCriterias] = useState<Criteria[]>([]);
  const [isTableReady, setIsTableReady] = useState(false);
  const [filters, setFilters] = useState<
    Array<{ field: string; value: string }>
  >([]);
  const [discrepCols] = useState(getDiscrepancyColumn(false, false));

  const [startDate, setStartDate] = useState<string | null>(() => {
    const savedFilters = localStorage.getItem("discrepancyRecords-dateRange");
    return savedFilters ? JSON.parse(savedFilters).startDate || null : null;
  });

  const [endDate, setEndDate] = useState<string | null>(() => {
    const savedFilters = localStorage.getItem("discrepancyRecords-dateRange");
    return savedFilters ? JSON.parse(savedFilters).endDate || null : null;
  });

  const [questionOnly, setQuestionOnly] = useState<boolean>(() => {
    const savedFilters = localStorage.getItem(
      "discrepancyRecords-questionOnly",
    );
    return savedFilters ? JSON.parse(savedFilters) === true : false;
  });

  const [showCompleted, setShowCompleted] = useState<boolean>(() => {
    const savedFilters = localStorage.getItem(
      "discrepancyRecords-showCompleted",
    );
    return savedFilters ? JSON.parse(savedFilters) === true : false;
  });

  const { user } = useContext(UserContext);
  const hasStatisticsPermission = user?.functions
    ? user.functions.includes("foresite:discrep:updateByJurisdiction") ||
      user.functions.includes("foresite:discrep:updateByCarrier")
    : false;

  console.log(criterias);

  useEffect(() => {
    // Try to restore filters from localStorage
    const savedFilters = localStorage.getItem("discrepancyRecords-criteria");
    if (savedFilters) {
      console.log("Restoring filters from localStorage", savedFilters);
      try {
        const filters = JSON.parse(savedFilters);
        if (Array.isArray(filters) && filters.length > 0) {
          const betweenFilters = filters.filter((f) => f.type === "BETWEEN");
          if (betweenFilters.length > 0) {
            setCriterias(betweenFilters);
          }

          // Set remaining filters as initial filters
          const headerFilters = filters.filter((f) => f.type !== "BETWEEN");
          if (headerFilters.length > 0) {
            setFilters(headerFilters);
          }
        }
      } catch (e) {
        console.error("Error restoring filters:", e);
      }
    }
    setIsTableReady(true);
  }, []);

  const navigate = useNavigate();
  const clickRow = (e, row) => {
    const data = row.getData();
    const cpn = data.CPN;

    if (cpn) {
      navigate(`${cpn}`);
    }
  };

  const onUpdateDateRange = (field: "start" | "end", value: string) => {
    if (field === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    // Store date range in localStorage
    localStorage.setItem(
      "discrepancyRecords-dateRange",
      JSON.stringify({
        startDate: field === "start" ? value : startDate,
        endDate: field === "end" ? value : endDate,
      }),
    );
    // update criterias state to trigger useEffect
    setCriterias((prev) => [...prev]);

    // force table refresh to apply filter
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.setData();
      }
    }, 100);
  };

  const onQuestionOnlyChange = () => {
    setQuestionOnly((prev) => !prev);
    // update it to be true or false
    localStorage.setItem(
      "discrepancyRecords-questionOnly",
      JSON.stringify(!questionOnly),
    );

    // update criterias state to trigger useEffect
    setCriterias((prev) => [...prev]);

    // force table refresh to apply filter
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.setData();
      }
    }, 100);
  };

  const onShowCompletedChange = () => {
    setShowCompleted((prev) => !prev);
    // update it to be true or false
    localStorage.setItem(
      "discrepancyRecords-showCompleted",
      JSON.stringify(!showCompleted),
    );

    // update criterias state to trigger useEffect
    setCriterias((prev) => [...prev]);

    // force table refresh to apply filter
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.setData();
      }
    }, 100);
  };

  const tableRef = useRef<any>(null);
  // set tabulator options
  const options = {
    rowFormatter: function (row) {
      var openQuestion = row.getData().question;
      if (openQuestion) {
        row.getElement().style.backgroundColor = "rgb(173, 216, 238)";
      }
    },
    layout: "fitColumns",
    movableColumns: true,
    placeholder: "No Discrepancies Found",
    // persist table state
    persistenceID: "DiscrepancyRecords1", // update ID param whenever table columns are updated
    persistenceMode: true, // store table state in local storage if possible, if not store in cookie
    persistence: {
      columns: ["width"], // persist column width; also persists column order
      page: true, // persist page number
    },
    // pagination
    pagination: true, // enable pagination
    paginationMode: "remote", // enable remote pagination
    paginationSize: pageSize, // default number of rows per page
    paginationSizeSelector: [10, 25, 50, 100], // page size options
    footerElement: "<span id='record-count'></span>",
    // filter
    filterMode: "remote", // send filter data to the server instead of processing locally
    initialHeaderFilter: filters,
    // sorting
    sortMode: "remote",
    initialSort: [{ column: "age", dir: "desc" }],
    // data request
    ajaxRequestFunc: function (url, config, params) {
      // convert default table params to API params
      let sortField = params.sort.length > 0 ? params.sort[0].field : "ANI";
      let sortDir = params.sort.length > 0 ? params.sort[0].dir : "asc";
      let firstRow = (params.page - 1) * params.size;
      let rangeStr = firstRow + "," + params.size;
      let savedCriterias = localStorage.getItem("discrepancyRecords-criteria");
      let dateRange = JSON.parse(
        localStorage.getItem("discrepancyRecords-dateRange") || "{}",
      );
      let questionOnly = JSON.parse(
        localStorage.getItem("discrepancyRecords-questionOnly") || "false",
      );
      let showCompleted = JSON.parse(
        localStorage.getItem("discrepancyRecords-showCompleted") || "false",
      );
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter((c) => c.type === "BETWEEN");
      }

      let reqBody = {
        range: rangeStr,
        sortBy: sortField,
        sortDirection: sortDir,
        criterias: [
          ...crit,
          ...params.filter.map((f) => ({
            field: f.field,
            type: f.type,
            value: Array.isArray(f.value) ? f.value[0] : f.value,
          })),
          ...(questionOnly
            ? [{ field: "question", type: "=", value: "1" }]
            : []),
          ...(dateRange.startDate && dateRange.endDate
            ? [
                {
                  field: "occurred",
                  type: "BETWEEN",
                  value: `${dateRange.startDate} 00:00:00,${dateRange.endDate} 23:59:59`,
                },
              ]
            : []),
        ],
        full: true,
      };

      // log request body
      console.log("Discrep table request body", reqBody);
      return new Promise(function (resolve, reject) {
        // fetch records
        client
          .getRecords(reqBody, url, showCompleted)
          .then((res) => {
            // success -> return data
            resolve(res.data);
          })
          .catch((err) => {
            // error -> return error message
            console.error("Discrep table error response", err);
            reject(err);
          });
      });
    },
    ajaxURL: "searchByCriteria", // API path
    ajaxResponse: (url, params, response) => {
      // log response
      console.log("Discrep table response", response);
      // pagination footer text
      let currPageSize = tableRef.current.getPageSize();
      let currPage = tableRef.current.getPage();
      updateFooterText(
        currPage,
        currPageSize,
        response.message.Size,
        response.message.TotalRecordCount,
      );
      let savedCriterias = localStorage.getItem("discrepancyRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter((c) => c.type === "BETWEEN");
      }

      // Store current filters if they exist
      let crits = [
        ...crit,
        ...params.filter.map((f) => ({
          field: f.field,
          type: f.type,
          value: Array.isArray(f.value) ? f.value[0] : f.value,
        })),
      ];

      if (crits && crits.length > 0) {
        console.log("Storing current criteria", crits);
        localStorage.setItem(
          "discrepancyRecords-criteria",
          JSON.stringify(crits),
        );
        setCriterias(crits);
      } else {
        console.log("Removing criteria");
        localStorage.removeItem("discrepancyRecords-criteria");
        setCriterias([]);
      }

      // return data to table
      let d = response.message.Size > 0 ? response.message.List : [];
      let last =
        response.message.Size > 0
          ? Math.ceil(response.message.TotalRecordCount / currPageSize)
          : 0;
      return {
        data: d,
        last_page: last,
      };
    },
  };

  const handleClearFilters = () => {
    localStorage.removeItem("discrepancyRecords-criteria");
    localStorage.removeItem("discrepancyRecords-showCompleted");
    localStorage.removeItem("discrepancyRecords-questionOnly");
    localStorage.removeItem("discrepancyRecords-dateRange");
    setCriterias([]);
    setFilters([]);

    setStartDate(null);
    setEndDate(null);
    setQuestionOnly(false);
    setShowCompleted(false);

    // Only attempt to clear filters if table is ready and ref exists
    if (isTableReady && tableRef.current) {
      tableRef.current.clearHeaderFilter();
      console.log("Cleared table filters");
    } else {
      console.log(
        "Table not ready or ref not found, filters cleared from state only",
      );
      // Force a table refresh to ensure filters are cleared
      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.setData();
        }
      }, 100);
    }
  };

  return (
    <>
      <div css={[tw`flex md:flex-nowrap flex-wrap justify-between gap-y-6`]}>
        <div css={[tw`flex flex-col space-y-4 w-full`]}>
          {criterias.some(
            (c) => c.type === "BETWEEN" && typeof c.value === "string",
          ) && (
            <div css={[tw`text-sm text-gray-600 font-medium`]}>
              {criterias
                .filter(
                  (c) => c.type === "BETWEEN" && typeof c.value === "string",
                )
                .map((c, index) => {
                  const [start, end] = (c.value as string).split(",");
                  return (
                    <span key={`${c.field}-${index}`}>
                      {`${c.field} Range: ${start} - ${end}`}
                      {index <
                      criterias.filter(
                        (c) =>
                          c.type === "BETWEEN" && typeof c.value === "string",
                      ).length -
                        1
                        ? " | "
                        : ""}
                    </span>
                  );
                })}
            </div>
          )}
          <div
            css={[
              tw`flex flex-row items-center justify-between space-x-4 overflow-x-auto`,
            ]}
          >
            <div css={[tw`flex flex-row items-center space-x-4`]}>
              <Button
                iconStart="search"
                onClick={() => tableRef.current?.setData()}
                variant="outline"
                kind="brand"
              >
                Filter
              </Button>
              <Button
                iconStart="x"
                onClick={handleClearFilters}
                variant="outline"
                kind="brand"
                disabled={
                  criterias.length === 0 &&
                  !startDate &&
                  !endDate &&
                  !questionOnly &&
                  !showCompleted
                }
              >
                Clear
              </Button>
              <FiltersDisplay criterias={criterias} cols={discrepCols} />
            </div>
            <div css={[tw`flex flex-row items-center space-x-2`]}>
              {hasStatisticsPermission && (
                <ExportButton
                  exportColumns={{}}
                  criterias={criterias}
                  type="statistical"
                  buttonName="Statistical"
                  dateRange={{ start: startDate, end: endDate }}
                />
              )}
              <ExportButton
                exportColumns={getDiscrepancyExportColumns()}
                criterias={criterias}
                type="discrepancy"
                showCompleted={showCompleted}
                questionOnly={questionOnly}
                dateRange={{ start: startDate, end: endDate }}
              />
            </div>
          </div>
          <div css={[tw`flex flex-row items-center space-x-4`]}>
            <div css={[tw`flex flex-col`]}>
              <p style={{ fontSize: "0.8rem" }}>From:</p>
              <input
                type="date"
                style={{
                  padding: "0.5rem",
                  border: "1px solid #ccc",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  color: startDate ? "black" : "#999",
                }}
                placeholder="Start Occurred"
                onChange={(e) => {
                  onUpdateDateRange("start", e.target.value);
                }}
                name="start"
                value={startDate ? startDate : ""}
              />
            </div>
            <div css={[tw`flex flex-col`]}>
              <p style={{ fontSize: "0.8rem" }}>To:</p>
              <input
                type="date"
                style={{
                  padding: "0.5rem",
                  border: "1px solid #ccc",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  color: endDate ? "black" : "#999",
                }}
                placeholder="End Occurred"
                onChange={(e) => {
                  onUpdateDateRange("end", e.target.value);
                }}
                name="end"
                value={endDate ? endDate : ""}
              />
            </div>
            <CalciteLabel
              layout="inline"
              css={[tw` rounded py-1 mt-7`]}
              style={{ cursor: "pointer", userSelect: "none" }}
              onClick={() => onQuestionOnlyChange()}
            >
              <input
                type="checkbox"
                checked={questionOnly}
                onChange={(e) => {
                  e.stopPropagation();
                  onQuestionOnlyChange();
                }}
                style={{ marginRight: "0.5rem" }}
                onClick={(e) => e.stopPropagation()}
              />
              Questions Only
            </CalciteLabel>
            <CalciteLabel
              layout="inline"
              css={[tw` rounded py-1 mt-7`]}
              style={{ cursor: "pointer", userSelect: "none" }}
              onClick={(e) => onShowCompletedChange()}
            >
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => {
                  e.stopPropagation();
                  onShowCompletedChange();
                }}
                style={{ marginRight: "0.5rem" }}
                onClick={(e) => e.stopPropagation()}
              />
              Show Completed
            </CalciteLabel>
          </div>
        </div>
      </div>
      <div css={[tw` min-h-full border border-gray-300 rounded-md`]}>
        <style>
          {`
            .tabulator-row.tabulator-selectable[style*="background-color: rgb(173, 216, 238)"]:hover {
              background-color: #94d8faff !important;
            }
            `}
        </style>
        {isTableReady ? (
          <ReactTabulator
            id="DiscrepancyLoader"
            ref={tableRef}
            onRef={(r) => {
              tableRef.current = r.current;
            }}
            events={{
              rowClick: clickRow,
              pageSizeChanged: function (pagesize) {
                setPageSize(pagesize);
              },
            }}
            options={options}
            columns={discrepCols}
          />
        ) : (
          <div css={[tw`flex justify-center items-center h-32`]}>
            <CalciteLoader scale="s" label="Loading records..." />
          </div>
        )}
      </div>
      <Outlet />
    </>
  );
};

export default DiscrepancyRecords;
